import request from '@/utils/request'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { getLegacyShapedCombos } from '@/services/combo/apis'
import { buildStockLedgerPayload, STOCK_LEDGER_MAX_PAGE_SIZE } from './payload'
import { toRepeatQuery } from './queryString'
import { BrandNameOption, GetBrandNameOptionsReq, GetGoodsCodeOptionsReq, GoodsCodeOption, SearchStockLedgerRes, StockLedgerFilter } from './types'

/**
 * ST10 재고수불 화면이 쓰는 엔드포인트. (1.0 ST03 재고수불의 신규 디자인 재구현 — LOGISPRD-10666)
 *
 * 동작별 배선:
 * | 동작     | 2.0 API                                | 1.0 API                         | 비고                    |
 * |----------|----------------------------------------|---------------------------------|-------------------------|
 * | 조회     | `POST /api/v1/get-stock-ledgers`       | /api/grid/search (ST03_HEAD)    | LOGISPRD-9594           |
 * | 초기화   | —                                      | —                               | client                  |
 * | 다운로드 | `POST /api/v1/get-stock-ledgers/excel` | — (1.0 은 FE 로컬 ExcelJS 생성) | LOGISPRD-9595 서버 전환 |
 *
 * 위 표 밖의 보조 조회 3건은 필터 옵션/서치헬퍼용이다(버튼 동작 아님):
 * - 상품코드 서치헬퍼 `GET /api/v1/master/products/goods-codes` — LOGISPRD-9955 (2.0)
 * - 브랜드명 서치헬퍼 `GET /api/v1/master/products/brand-names` — LOGISPRD-14184 (2.0, 1.0 grid/search BRD_NM 대체)
 * - 센터명 콤보 `GET /api/v1/combos?comboCodes=WH_NM` — 2.0 (LOGISPRD-14161, 1.0 `/api/common/combo` 대체)
 */
export const STOCK_LEDGERS_URL = '/api/v1/get-stock-ledgers'
export const STOCK_LEDGERS_EXCEL_URL = `${STOCK_LEDGERS_URL}/excel`
export const GOODS_CODE_OPTIONS_URL = '/api/v1/master/products/goods-codes'
export const BRAND_NAME_OPTIONS_URL = '/api/v1/master/products/brand-names'

const userId = () => AuthStorage.user.get().email ?? ''

/**
 * ST03 재고수불 조회. (LOGISPRD-9594)
 *
 * 요청은 1.0 grid/search 호환 stringified 페이로드, 응답은 1.0 `{ rtnChk, rtnData }` 호환이다.
 * - 0건은 **HTTP 200 + 빈 배열** — 1.0 의 400/E15 와 다른 의도적 변경(LLD §2.3). 빈 배열 기준으로 처리할 것.
 * - 실패는 HTTP 400 + `{ rtnChk: false, message }` 로 온다(reject). 목록 조회 실패는 전역 알럿(meta.errorToast)이 message 를 보여 준다.
 * - 서버 페이징 — 요청한 한 페이지(기본 BE 상한 `STOCK_LEDGER_MAX_PAGE_SIZE`행)만 받고, 전체 그룹 수는 응답 `params.totalCnt` 로 온다 (LOGISPRD-14929).
 *   화면은 이 페이지를 이어 받아 전량을 본다(LOGISPRD-15270).
 *   BE 는 한 페이지를 10,000행까지만 준다. 엑셀은 이 API 가 아니라 서버가 전량(최대 100,000행)을 만든다.
 */
export const getStockLedgers = (filter: StockLedgerFilter, page = 1, pageSize = STOCK_LEDGER_MAX_PAGE_SIZE) =>
  request.post<SearchStockLedgerRes, GridApiRequestBody>(STOCK_LEDGERS_URL, buildStockLedgerPayload(filter, userId(), undefined, { page, pageSize }))

/**
 * 상품코드 서치헬퍼 목록. (LOGISPRD-9955 — 1.0 `GDS_CD_SearchForm` 이관)
 *
 * 무페이징 distinct 목록이라 응답 전건이 모달 그리드에 실린다. 화주로 범위를 좁혀 조회한다.
 */
export const getGoodsCodeOptions = (params: GetGoodsCodeOptionsReq) =>
  request.get<ApiResponseV2<{ items: GoodsCodeOption[] }>>(`${GOODS_CODE_OPTIONS_URL}?${toRepeatQuery(params)}`)

/**
 * 브랜드명 서치헬퍼 목록. (LOGISPRD-14184 — 1.0 `BRD_NM_SearchForm` 대체)
 *
 * master_item distinct 브랜드명, 브랜드명 부분일치·오름차순, 최대 1,000행. 0건은 HTTP 200 + 빈 목록이다.
 */
export const getBrandNameOptions = (params: GetBrandNameOptionsReq) =>
  request.get<ApiResponseV2<{ items: BrandNameOption[] }>>(`${BRAND_NAME_OPTIONS_URL}?${toRepeatQuery(params)}`)

/** 센터명 콤보. 콤보관리(MOMS_COMBO_INFO) `WH_NM` 를 2.0 콤보 API 로 받아 1.0 모양으로 돌려준다 (LOGISPRD-14161). */
export const getWarehouseNameCombo = () => getLegacyShapedCombos(['WH_NM'])
